import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, IconButton, Chip, 
  CircularProgress, FormControl, InputLabel, Select, MenuItem,
  Alert, Tabs, Tab, Card, CardContent
} from '@mui/material';
import { 
  Add, Edit, Delete, Refresh, CheckCircle, Cancel, 
  EventNote, People, TrendingUp, TrendingDown, AccessTime
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const Attendance = () => {
  const [tabValue, setTabValue] = useState(0);
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedShiftDetails, setSelectedShiftDetails] = useState(null);
  
  const [formData, setFormData] = useState({
    employee: '',
    shift: '',
    date: new Date().toISOString().split('T')[0],
    status: 'present',
    check_in_time: '',
    check_out_time: '',
    overtime_hours: 0,
    late_minutes: 0,
    early_leave_minutes: 0,
    notes: ''
  });

  const STATUS_COLORS = {
    present: 'success',
    absent: 'error',
    leave: 'warning',
    half_day: 'info',
    holiday: 'primary',
    weekend: 'default',
    late: 'warning'
  };

  const STATUS_LABELS = {
    present: 'Present',
    absent: 'Absent',
    leave: 'Leave',
    half_day: 'Half Day',
    holiday: 'Holiday',
    weekend: 'Weekend',
    late: 'Late'
  };

  useEffect(() => {
    fetchEmployees();
    fetchShifts();
    fetchAttendance();
    fetchSummary();
  }, [selectedMonth, selectedYear, selectedEmployee]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/hr/employees/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setEmployees(data);
    } catch (error) {
      toast.error('Failed to fetch employees');
    }
  };

  const fetchShifts = async () => {
    try {
      const response = await api.get('/hr/shifts/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setShifts(data.filter(s => s.is_active));
    } catch (error) {
      toast.error('Failed to fetch shifts');
    }
  };

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      let url = `/hr/attendance/?month=${selectedMonth}&year=${selectedYear}`;
      if (selectedEmployee) {
        url += `&employee_id=${selectedEmployee}`;
      }
      const response = await api.get(url);
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setAttendance(data);
    } catch (error) {
      toast.error('Failed to fetch attendance');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      let url = `/hr/attendance/summary/?month=${selectedMonth}&year=${selectedYear}`;
      if (selectedEmployee) {
        url += `&employee_id=${selectedEmployee}`;
      }
      const response = await api.get(url);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleShiftChange = (shiftId) => {
    const selectedShift = shifts.find(s => s.id === shiftId);
    setSelectedShiftDetails(selectedShift);
    
    // Auto-fill check-in and check-out times based on shift timings
    if (selectedShift) {
      setFormData({
        ...formData,
        shift: shiftId,
        check_in_time: selectedShift.start_time || '',
        check_out_time: selectedShift.end_time || ''
      });
    } else {
      setFormData({
        ...formData,
        shift: shiftId,
        check_in_time: '',
        check_out_time: ''
      });
    }
  };

  const handleOpenDialog = (record = null) => {
    if (record) {
      setEditingRecord(record);
      const shiftDetails = shifts.find(s => s.id === record.shift);
      setSelectedShiftDetails(shiftDetails || null);
      setFormData({
        employee: record.employee,
        shift: record.shift || '',
        date: record.date,
        status: record.status,
        check_in_time: record.check_in_time || '',
        check_out_time: record.check_out_time || '',
        overtime_hours: record.overtime_hours || 0,
        late_minutes: record.late_minutes || 0,
        early_leave_minutes: record.early_leave_minutes || 0,
        notes: record.notes || ''
      });
    } else {
      setEditingRecord(null);
      setSelectedShiftDetails(null);
      const defaultShift = shifts.length > 0 ? shifts[0].id : '';
      const defaultShiftDetails = shifts.length > 0 ? shifts[0] : null;
      setSelectedShiftDetails(defaultShiftDetails);
      setFormData({
        employee: '',
        shift: defaultShift,
        date: new Date().toISOString().split('T')[0],
        status: 'present',
        check_in_time: defaultShiftDetails?.start_time || '',
        check_out_time: defaultShiftDetails?.end_time || '',
        overtime_hours: 0,
        late_minutes: 0,
        early_leave_minutes: 0,
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRecord(null);
    setSelectedShiftDetails(null);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.employee) {
        toast.error('Please select an employee');
        return;
      }

      const submitData = {
        ...formData,
        overtime_hours: parseFloat(formData.overtime_hours) || 0,
        late_minutes: parseInt(formData.late_minutes) || 0,
        early_leave_minutes: parseInt(formData.early_leave_minutes) || 0
      };

      if (editingRecord) {
        await api.put(`/hr/attendance/${editingRecord.id}/`, submitData);
        toast.success('Attendance updated successfully');
      } else {
        await api.post('/hr/attendance/', submitData);
        toast.success('Attendance recorded successfully');
      }
      handleCloseDialog();
      fetchAttendance();
      fetchSummary();
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error('Failed to save attendance');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await api.delete(`/hr/attendance/${id}/`);
        toast.success('Attendance record deleted');
        fetchAttendance();
        fetchSummary();
      } catch (error) {
        toast.error('Failed to delete record');
      }
    }
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    if (emp) {
      return emp.full_name || emp.employee_id;
    }
    return 'N/A';
  };

  const getShiftName = (shiftId) => {
    const shift = shifts.find(s => s.id === shiftId);
    return shift ? shift.name : 'N/A';
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const SummaryCard = ({ title, value, icon, color }) => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="subtitle2">
              {title}
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {value}
            </Typography>
          </Box>
          <Box sx={{ bgcolor: color + '20', borderRadius: '50%', p: 1 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          <EventNote sx={{ mr: 1, verticalAlign: 'middle' }} />
          Attendance Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Mark Attendance
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                label="Month"
              >
                {months.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                label="Year"
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Employee</InputLabel>
              <Select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                label="Employee"
              >
                <MenuItem value="">All Employees</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.full_name || emp.employee_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => { fetchAttendance(); fetchSummary(); }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard
              title="Present"
              value={summary.total_present || 0}
              icon={<CheckCircle sx={{ color: '#2e7d32' }} />}
              color="#2e7d32"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard
              title="Absent"
              value={summary.total_absent || 0}
              icon={<Cancel sx={{ color: '#d32f2f' }} />}
              color="#d32f2f"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard
              title="Leave"
              value={summary.total_leave || 0}
              icon={<EventNote sx={{ color: '#ed6c02' }} />}
              color="#ed6c02"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard
              title="Half Day"
              value={summary.total_half_day || 0}
              icon={<TrendingDown sx={{ color: '#0288d1' }} />}
              color="#0288d1"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard
              title="Overtime"
              value={`${summary.total_overtime_hours || 0}h`}
              icon={<AccessTime sx={{ color: '#1976d2' }} />}
              color="#1976d2"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <SummaryCard
              title="Total Days"
              value={summary.total_days || 0}
              icon={<People sx={{ color: '#9c27b0' }} />}
              color="#9c27b0"
            />
          </Grid>
        </Grid>
      )}

      {/* Attendance Table */}
      <Paper sx={{ p: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Shift</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Check In</TableCell>
                <TableCell>Check Out</TableCell>
                <TableCell align="right">Overtime (hrs)</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : attendance.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary">No attendance records found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                attendance.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>{getEmployeeName(record.employee)}</TableCell>
                    <TableCell>{new Date(record.date).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getShiftName(record.shift)}
                        size="small"
                        variant="outlined"
                        icon={<AccessTime />}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={STATUS_LABELS[record.status] || record.status}
                        color={STATUS_COLORS[record.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{record.check_in_time || '-'}</TableCell>
                    <TableCell>{record.check_out_time || '-'}</TableCell>
                    <TableCell align="right">{record.overtime_hours || 0}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog(record)}>
                        <Edit />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(record.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Attendance Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRecord ? 'Edit Attendance' : 'Mark Attendance'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Employee</InputLabel>
                <Select
                  value={formData.employee}
                  onChange={(e) => setFormData({...formData, employee: e.target.value})}
                  label="Employee"
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.full_name || emp.employee_id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Shift</InputLabel>
                <Select
                  value={formData.shift}
                  onChange={(e) => handleShiftChange(e.target.value)}
                  label="Shift"
                >
                  {shifts.map((shift) => (
                    <MenuItem key={shift.id} value={shift.id}>
                      {shift.name} ({shift.start_time} - {shift.end_time})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  label="Status"
                >
                  <MenuItem value="present">Present</MenuItem>
                  <MenuItem value="absent">Absent</MenuItem>
                  <MenuItem value="leave">Leave</MenuItem>
                  <MenuItem value="half_day">Half Day</MenuItem>
                  <MenuItem value="holiday">Holiday</MenuItem>
                  <MenuItem value="weekend">Weekend</MenuItem>
                  <MenuItem value="late">Late</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Shift Timings Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                <AccessTime sx={{ mr: 1, verticalAlign: 'middle' }} />
                Shift Timings (Auto-filled from shift selection)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Check In Time"
                type="time"
                value={formData.check_in_time}
                onChange={(e) => setFormData({...formData, check_in_time: e.target.value})}
                InputLabelProps={{ shrink: true }}
                helperText={selectedShiftDetails ? `Shift: ${selectedShiftDetails.name}` : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Check Out Time"
                type="time"
                value={formData.check_out_time}
                onChange={(e) => setFormData({...formData, check_out_time: e.target.value})}
                InputLabelProps={{ shrink: true }}
                helperText={selectedShiftDetails ? `Shift: ${selectedShiftDetails.name}` : ''}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="secondary" sx={{ mb: 1 }}>
                Additional Details
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Overtime Hours"
                type="number"
                value={formData.overtime_hours}
                onChange={(e) => setFormData({...formData, overtime_hours: parseFloat(e.target.value)})}
                inputProps={{ step: 0.5, min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Late Minutes"
                type="number"
                value={formData.late_minutes}
                onChange={(e) => setFormData({...formData, late_minutes: parseInt(e.target.value) || 0})}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Early Leave Minutes"
                type="number"
                value={formData.early_leave_minutes}
                onChange={(e) => setFormData({...formData, early_leave_minutes: parseInt(e.target.value) || 0})}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingRecord ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Attendance;